import { type ReactNode, useCallback, useRef, useState } from 'react';
import { closestCenter, DndContext } from '@dnd-kit/core';
import { rectSortingStrategy, SortableContext } from '@dnd-kit/sortable';

import type { ActionMenuItem } from '@rover/components/src/callouts/ActionMenu';
import { Box } from '@rover/kibble/core';
import LoadingShimmer from '@rover/kibble/patterns/LoadingShimmer';
import MediaQuery from '@rover/kibble/styles/MediaQuery';
import useOnIntersect from '@rover/react-lib/src/hooks/useOnIntersect';

import AddImagesTile from './AddImagesTile';
import {
  ACTIONS_SELECTOR,
  GALLERY_CONTAINER_WIDTH,
  GALLERY_GRID_GAP,
  GALLERY_MIN_TILE_SIZE,
} from './constants';
import GalleryPrivateBadge from './GalleryPrivateBadge';
import GalleryTile from './GalleryTile';
import type { GalleryItem, PaginationConfig } from './ImageGallery.common';
import {
  countGalleryVideoItems,
  galleryItemKey,
  isDimmedImage,
  isEditableGalleryImage,
} from './ImageGallery.common';
import SortableImageTile from './SortableImageTile';
import useGridReorder from './useGridReorder';
import { getShimmerCount, shouldLoadMore } from './utils';

type ImageGridContainerProps = {
  images: GalleryItem[];
  onImageClick: (index: number) => void;
  // Mobile only props
  // eslint-disable-next-line react/no-unused-prop-types
  onClose?: () => void;
  // eslint-disable-next-line react/no-unused-prop-types
  virtualizedGrid?: boolean;
  pagination?: PaginationConfig;
  renderImageOverlay?: (index: number) => ReactNode;
  onImagesAdded?: (files: File[]) => void;
  isUploading?: boolean;
  renderImageActions?: (index: number) => ActionMenuItem[];
  columns?: number;
  onReorder?: (fromIndex: number, toIndex: number) => void;
  isReorderable?: (index: number) => boolean;
  dimmedImageIds?: Set<number>;
  isManaging?: boolean;
  selectedIds?: Set<number>;
  onToggleSelected?: (imageId: number) => void;
};

const ImageGridContainer = ({
  images,
  onImageClick,
  pagination,
  renderImageOverlay,
  onImagesAdded,
  isUploading,
  renderImageActions,
  columns,
  onReorder,
  isReorderable,
  dimmedImageIds,
  isManaging = false,
  selectedIds,
  onToggleSelected,
}: ImageGridContainerProps): JSX.Element | null => {
  const [openMenuIndex, setOpenMenuIndex] = useState<number | null>(null);
  const gridRef = useRef<HTMLElement | null>(null);

  const handleLoadMore = useCallback(() => {
    if (shouldLoadMore(pagination)) {
      pagination?.onLoadMore();
    }
  }, [pagination]);

  const sentinelRef = useOnIntersect(handleLoadMore, false);

  const { sensors, sortableKeys, canDrag, onDragEnd } = useGridReorder({
    images,
    onReorder,
    isReorderable,
    gridRef,
  });

  const loadedImageCount = images.length - countGalleryVideoItems(images);
  const shimmerCount = getShimmerCount(pagination, loadedImageCount);

  const actionsRevealSx = (index: number): Record<string, unknown> => ({
    '@media (hover: hover)': {
      [`& ${ACTIONS_SELECTOR}`]: { opacity: openMenuIndex === index ? 1 : 0 },
      [`&:hover ${ACTIONS_SELECTOR}, & ${ACTIONS_SELECTOR}:focus-within`]: {
        opacity: 1,
      },
    },
  });

  const grid = (
    <>
      <Box
        ref={gridRef}
        data-testid="gallery-grid"
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(auto-fill, minmax(${GALLERY_MIN_TILE_SIZE}, 1fr))`,
          ...(columns
            ? {
                [MediaQuery.SM_UP.toString()]: {
                  gridTemplateColumns: `repeat(${columns}, 1fr)`,
                },
              }
            : {}),
          gap: GALLERY_GRID_GAP,
          margin: '0 auto',
          justifyContent: 'center',
          width: '100%',
          maxWidth: GALLERY_CONTAINER_WIDTH,
        }}
      >
        {onImagesAdded && <AddImagesTile onImagesAdded={onImagesAdded} isUploading={isUploading} />}
        {images.map((image, index) => {
          const key = galleryItemKey(image);
          const isDimmed = isDimmedImage(image, dimmedImageIds);
          const isEditable = isEditableGalleryImage(image);
          const imageId = image.id ?? undefined;
          const isSelectable = isManaging && isEditable && imageId !== undefined;
          const tile = (
            <GalleryTile
              image={image}
              index={index}
              onImageClick={onImageClick}
              overlay={
                renderImageOverlay?.(index) ??
                (isEditable && isDimmed ? <GalleryPrivateBadge /> : null)
              }
              actions={renderImageActions?.(index)}
              onActionsOpenChange={(isOpen) => setOpenMenuIndex(isOpen ? index : null)}
              isDimmed={isDimmed}
              isManaging={isManaging}
              isSelectable={isSelectable}
              isSelected={isSelectable && !!selectedIds?.has(imageId)}
              onToggleSelected={
                isSelectable && imageId !== undefined
                  ? () => onToggleSelected?.(imageId)
                  : undefined
              }
            />
          );

          return canDrag(index) ? (
            <SortableImageTile key={key} id={key} position={index + 1} sx={actionsRevealSx(index)}>
              {tile}
            </SortableImageTile>
          ) : (
            <Box key={key} position="relative" width="100%" sx={actionsRevealSx(index)}>
              {tile}
            </Box>
          );
        })}
        {pagination?.isLoadingMore &&
          Array.from({ length: shimmerCount }, (_, i) => (
            <Box key={`shimmer-${i}`} width="100%" sx={{ aspectRatio: '1' }}>
              <LoadingShimmer
                width="100%"
                height="100%"
                borderRadius="primary"
                m="0x"
                data-testid="loading-more-shimmer"
              />
            </Box>
          ))}
      </Box>
      {pagination?.hasMore && (
        <Box ref={sentinelRef} height="1px" aria-hidden="true" data-testid="scroll-sentinel" />
      )}
    </>
  );

  if (!onReorder) {
    return grid;
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={sortableKeys} strategy={rectSortingStrategy}>
        {grid}
      </SortableContext>
    </DndContext>
  );
};

export default ImageGridContainer;
